import React, { useState } from 'react';
import { Avatar, Badge, Skeleton, Button, Upload } from 'antd';
import { UserOutlined, UploadOutlined, DeleteOutlined, CheckOutlined } from '@ant-design/icons';
import type { RcFile } from 'antd/es/upload/interface';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt, faPen } from '@fortawesome/free-solid-svg-icons';
import {
  ProfileCard,
  ProfileHeader,
  ProfileInfo,
  ProfileDetails,
  ProfileName,
  ProfileUsername,
  ProfileLocation,
  EditProfileButton,
  SaveChangesButton,
  CancelButton,
  ActionButtons,
  LogoUploadContainer,
  LogoPreviewBox,
  LogoActionsBox,
  UploadButtonGroup,
  UploadHint,
  UploadSectionTitle,
  ProfileActionGroup,
  ProfileUploadSection,
  ProfileLogoAvatar,
  UploadErrorText,
} from '../Styles';
import type { ProfileSectionProps } from '../types';
import { ROLE_CONFIG, getLogoUrl } from '../constants';
import ImageCropModal from '../../../commonComponents/ImageCropModal';

const FILE_VALIDATIONS = [
  { check: (f: RcFile) => !f.type.startsWith('image/'), message: 'Please select an image file' },
  { check: (f: RcFile) => f.size > 5 * 1024 * 1024,    message: 'Image size should be less than 5MB' },
];

const ProfileSection: React.FC<ProfileSectionProps> = ({
  user,
  role,
  loading,
  profileLoading,
  onEdit,
  onLogoChange,
  onSaveProfile,
  onCancelProfile,
  isUploading,
  setIsUploading,
}) => {
  const logoUrl = getLogoUrl(user);
  const [imageError, setImageError] = React.useState(false);
  const [previewLogo, setPreviewLogo] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [cropSrc, setCropSrc] = useState<string | null>(null);

  const beforeUpload = (file: RcFile) => {
    setUploadError(null);
    const failed = FILE_VALIDATIONS.find((v) => v.check(file));
    if (failed) { setUploadError(failed.message); return false; }
    setCropSrc(URL.createObjectURL(file));
    return false;
  };

  const handleCropDone = (file: File, previewUrl: string) => {
    setCropSrc(null);
    setPreviewLogo(previewUrl);
    onLogoChange?.(file);
  };

  const handleCropCancel = () => setCropSrc(null);

  const handleRemoveLogo = () => {
    setPreviewLogo(null);
    setUploadError(null);
    onLogoChange?.(null);
  };

  const handleCancel = () => {
    setPreviewLogo(null);
    setIsUploading(false);
    setUploadError(null);
    setImageError(false);
    onCancelProfile?.();
  };

  const avatarSrc = logoUrl && !imageError
    ? logoUrl
    : user
      ? `https://ui-avatars.com/api/?name=${user.name || user.username}&background=0D9488&color=fff&size=128`
      : undefined;

  const roleCfg = ROLE_CONFIG[role as keyof typeof ROLE_CONFIG];

  return (
    <>
    <ProfileCard>
      <ProfileHeader>
        <ProfileInfo>
          {profileLoading ? (
            <Skeleton.Avatar active size={80} shape="circle" />
          ) : (
            <Avatar
              size={80}
              src={avatarSrc}
              icon={<UserOutlined />}
              onError={() => { setImageError(true); return false; }}
            />
          )}
          <ProfileDetails>
            {profileLoading ? (
              <>
                <Skeleton.Input active size="small" style={{ width: 150, height: 24, marginBottom: 8 }} />
                <Skeleton.Input active size="small" style={{ width: 120, height: 20, marginBottom: 8 }} />
                <Skeleton.Input active size="small" style={{ width: 200, height: 20 }} />
              </>
            ) : user ? (
              <>
                <ProfileName level={3}>{user.name || user.username}</ProfileName>
                <ProfileUsername>@{user.username}</ProfileUsername>
                <ProfileLocation>
                  <FontAwesomeIcon icon={faMapMarkerAlt} />
                  {user.address || 'Location not provided'}
                </ProfileLocation>
                <Badge
                  color={roleCfg?.color || 'purple'}
                  text={roleCfg?.label || 'Administrator'}
                  style={{ marginTop: 8 }}
                />
              </>
            ) : null}
          </ProfileDetails>
        </ProfileInfo>

        <ProfileActionGroup>
          <EditProfileButton
            icon={<FontAwesomeIcon icon={faPen} style={{ fontSize: 14 }} />}
            onClick={() => { setIsUploading(!isUploading); onEdit?.(); }}
            disabled={loading || profileLoading}
          >
            {isUploading ? 'Close' : 'Edit Profile'}
          </EditProfileButton>
        </ProfileActionGroup>
      </ProfileHeader>

      {isUploading && (
        <ProfileUploadSection>
          <UploadSectionTitle>Upload Logo</UploadSectionTitle>
          <LogoUploadContainer>
            <LogoPreviewBox>
              <ProfileLogoAvatar size={100} src={previewLogo || undefined} icon={<UserOutlined />} />
            </LogoPreviewBox>
            <LogoActionsBox>
              <UploadButtonGroup>
                <Upload maxCount={1} beforeUpload={beforeUpload} accept="image/*" showUploadList={false}>
                  <Button icon={<UploadOutlined />}>Upload Logo</Button>
                </Upload>
                {(previewLogo || logoUrl) && (
                  <Button icon={<DeleteOutlined />} danger onClick={handleRemoveLogo}>Remove</Button>
                )}
              </UploadButtonGroup>
              <UploadHint>Recommended: Square image, max 5MB. JPG, PNG, or GIF format.</UploadHint>
              {uploadError && <UploadErrorText>{uploadError}</UploadErrorText>}
            </LogoActionsBox>
          </LogoUploadContainer>
          <ActionButtons>
            <SaveChangesButton icon={<CheckOutlined />} onClick={onSaveProfile} loading={loading}>
              Save Changes
            </SaveChangesButton>
            <CancelButton onClick={handleCancel}>Cancel</CancelButton>
          </ActionButtons>
        </ProfileUploadSection>
      )}
    </ProfileCard>

    {cropSrc && (
      <ImageCropModal
        open={!!cropSrc}
        imageSrc={cropSrc}
        aspect={1}
        onCropDone={handleCropDone}
        onCancel={handleCropCancel}
      />
    )}
  </>
  );
};

export default React.memo(ProfileSection);
